import { createFileRoute, Link, redirect, useRouteContext } from "@tanstack/react-router";
import { type JSX, useEffect, useRef } from "react";
import { z } from "zod";

import ArrowRight from "@repo/icons/default/ArrowRight";

import { FirstScheduleIntroPreview } from "@/components/onboarding/first-schedule-intro-preview";
import { Button } from "@/components/ui/button/button";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { useRecordFirstScheduleEntry } from "@/hooks/use-first-schedule-history";
import { FIRST_SCHEDULE_INTRO_ORIGINS } from "@/hooks/use-first-schedule-intro-destination";
import { trackEvent } from "@/lib/tracking";

const firstScheduleIntroSearchSchema = z.object({
  origem: z.enum(FIRST_SCHEDULE_INTRO_ORIGINS).optional().catch(undefined),
});

const FirstScheduleIntroPage = (): JSX.Element => {
  const { user } = useRouteContext({ from: "__root__" });
  const { origem = "link_direto" } = Route.useSearch();
  useRecordFirstScheduleEntry("intro");

  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (hasTrackedView.current) {
      return;
    }

    hasTrackedView.current = true;
    trackEvent("Introducao Primeira Agenda Visualizada", { origem });
  }, [origem]);

  const trackButtonClick = (cta: "disponibilizar_agenda" | "fazer_depois"): void => {
    trackEvent("Introducao Primeira Agenda Botao Clicado", { cta, origem });
  };

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
      </FlowHeader>

      <Page
        className="flex flex-col justify-between gap-4 pb-0 px-6"
        showCustomerIoContainer={false}
      >
        <div className="flex flex-col gap-4">
          <FirstScheduleIntroPreview professionalName={user.apelido ?? user.nome} />

          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-medium text-neutral-700 leading-[1.32]">
              Seus futuros pacientes estão buscando horários
            </h1>
            <p className="text-base text-neutral-600 leading-[1.32]">
              Disponibilize sua agenda em poucos minutos e sem custo.
            </p>
          </div>
        </div>

        <div className="sticky bottom-0 flex flex-col gap-3 pt-3 pb-4 bg-white">
          <Button asChild fullWidth>
            <Link
              to="/onboarding/first-schedule"
              className="w-full"
              onClick={() => trackButtonClick("disponibilizar_agenda")}
            >
              <div className="flex flex-row items-center justify-center gap-2">
                Disponibilizar agenda
                <ArrowRight size={24} aria-hidden="true" />
              </div>
            </Link>
          </Button>
          <Link
            to="/"
            replace
            className="w-full"
            onClick={() => trackButtonClick("fazer_depois")}
          >
            <Button color="secondaryLight" fullWidth>
              Fazer depois
            </Button>
          </Link>
        </div>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/first-schedule/intro")({
  validateSearch: firstScheduleIntroSearchSchema,
  component: FirstScheduleIntroPage,
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-introducao-da-primeira-agenda"]) {
      redirect({ to: "/onboarding/first-schedule", throw: true });
    }
  },
});
